import { Flex, Typography } from 'antd'
import { useTranslation } from 'react-i18next'

import { useStyles } from 'src/features/auth/styles'

import { QR_PANEL_STATUS } from './constants'
import { useQrSignInFlow } from './hooks'
import { QrApprovedNotice } from './QrApprovedNotice'
import { QrCodeBox } from './QrCodeBox'
import { QrCompactCaption } from './QrCompactCaption'
import { QrFullActions } from './QrFullActions'
import { QrTerminalNotice } from './QrTerminalNotice'
import { QrWaitingStatus } from './QrWaitingStatus'
import type { QrSignInPanelProps } from './types'
import { isWaitingStatus } from './utils'

const { Title, Text } = Typography

export const QrSignInPanel = ({
  domainName,
  headingLevel = 1,
  variant = 'full',
  showPasswordToggle,
  onSwitchToPassword,
  onAuthenticated
}: QrSignInPanelProps) => {
  const { t } = useTranslation()
  const { styles } = useStyles()
  const {
    state,
    secondsRemaining,
    progressPercent,
    isStarting,
    terminalStatus,
    shouldShowCompactRefreshOverlay,
    isRefreshPending,
    errorAlert,
    handleCancel,
    handleRefresh,
    handleRetry,
    handleSwitchToPassword
  } = useQrSignInFlow({
    domainName,
    variant,
    onSwitchToPassword,
    onAuthenticated
  })
  const qrCode = (
    <QrCodeBox
      variant={variant}
      status={state.status}
      qrPayload={state.session?.qr_payload}
      shouldShowRefreshOverlay={shouldShowCompactRefreshOverlay}
      isRefreshPending={isRefreshPending}
      onRefresh={handleRefresh}
      onRetry={handleRetry}
    />
  )

  if (variant === 'compact') {
    return (
      <Flex
        vertical
        gap={12}
        className={styles.qrCompactPanel}
        align="center"
        justify="center"
      >
        {qrCode}
        <QrCompactCaption />
      </Flex>
    )
  }

  return (
    <Flex orientation="vertical" gap={28} className={styles.qrPanel}>
      <Flex orientation="vertical" gap={10}>
        <Title level={headingLevel}>{t('auth.qrSignIn.title')}</Title>
        <Text type="secondary">{t('auth.qrSignIn.subtitle')}</Text>
      </Flex>

      {errorAlert}

      {qrCode}

      {state.session && isWaitingStatus(state.status) && (
        <QrWaitingStatus
          status={state.status}
          progressPercent={progressPercent}
          secondsRemaining={secondsRemaining}
        />
      )}

      {terminalStatus && <QrTerminalNotice status={terminalStatus} />}

      {state.status === QR_PANEL_STATUS.APPROVED && <QrApprovedNotice />}

      <QrFullActions
        isTerminal={Boolean(terminalStatus)}
        isApproved={state.status === QR_PANEL_STATUS.APPROVED}
        hasSession={Boolean(state.session)}
        isStarting={isStarting}
        showPasswordToggle={showPasswordToggle}
        onCancel={handleCancel}
        onRetry={handleRetry}
        onSwitchToPassword={
          onSwitchToPassword ? handleSwitchToPassword : undefined
        }
      />
    </Flex>
  )
}
